import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { sendApiRequest } from '../utils';
import { useSelector } from 'react-redux';
import {
  FiUsers,
  FiUserPlus,
  FiAward,
  FiSearch,
  FiChevronRight,
  FiChevronDown,
  FiMapPin,
  FiPhone,
  FiMail,
  FiStar,
  FiTrendingUp,
  FiActivity,
  FiRefreshCw,
  FiUserCheck,
  FiUserX,
  FiInfo,
  FiDownload,
  FiFilter
} from 'react-icons/fi';
import {
  FaUserCircle,
  FaCrown,
  FaMedal,
  FaCertificate,
  FaTree,
  FaNetworkWired,
  FaHandshake,
  FaGem
} from 'react-icons/fa';

const NetworkExplorer = () => {
  const userData = useSelector((state) => state.user_info[0]?.user);

  // State
  const [expandedNodes, setExpandedNodes] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [networkData, setNetworkData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [networkStats, setNetworkStats] = useState({
    totalMembers: 0,
    directReferrals: 0,
    yourSponsor: '-',
    yourRank: '-',
  });

  // Fetch network data
  useEffect(() => {
    const fetchData = async () => {
      if (!userData?._id) {
        setError('User ID not found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await sendApiRequest(`api/admin/partners/${userData._id}`, {}, 'GET');

        if (response.data?.success) {
          const data = response.data.data;

          // Create network tree structure with recursive children
          const processMLMNode = (mlmNode) => {
            if (!mlmNode) return null;
            
            return {
              id: mlmNode.user._id,
              name: mlmNode.user.fullName || 'Unknown',
              sponsorCode: mlmNode.user.sponsorCode || '-',
              rank: mlmNode.user.rank || '-',
              phone: mlmNode.user.phone || '-',
              email: mlmNode.user.email || '-',
              city: mlmNode.user.city || '-',
              state: mlmNode.user.state || '-',
              country: mlmNode.user.country || '-',
              status: mlmNode.user.status || 'active',
              level: mlmNode.level || 1,
              business: mlmNode.user.totalBusiness || 0,
              children: mlmNode.children?.map(child => processMLMNode(child)) || []
            };
          };

          const networkTree = {
            id: data._id,
            name: data.fullName || 'Unknown',
            sponsorCode: data.sponsorCode || '-',
            city: data.city || '-',
            state: data.state || '-',
            country: data.country || '-',
            rank: data.rank || '-',
            phone: data.phone || '-',
            email: data.email || '-',
            status: data.status || 'active',
            level: 0,
            business: data.totalBusiness || 0,
            parent: data.mlmTree?.parent
              ? {
                  id: data.mlmTree.parent._id,
                  name: data.mlmTree.parent.fullName || 'Unknown',
                  sponsorCode: data.mlmTree.parent.sponsorCode || '-',
                  rank: data.mlmTree.parent.rank || '-',
                  phone: data.mlmTree.parent.phone || '-',
                  email: data.mlmTree.parent.email || '-',
                }
              : null,
            children: data.mlmTree?.children?.map(child => processMLMNode(child)) || []
          };

          // Calculate total members recursively including all levels
          const countMembers = (node) => {
            let count = 1;
            if (node.children) {
              count += node.children.reduce((sum, child) => sum + countMembers(child), 0);
            }
            return count;
          };

          setNetworkData(networkTree);

          // Update network stats with complete tree count
          setNetworkStats({
            totalMembers: countMembers(networkTree),
            directReferrals: networkTree.children.length,
            yourSponsor: data.sponsor || '-',
            yourRank: networkTree.rank || '-',
          });
        } else {
          const message = response.data?.message || 'Failed to fetch network data';
          setError(message);
          toast.error(message);
        }
      } catch (err) {
        const message = err.message || 'Failed to load network data';
        setError(message);
        toast.error(message);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userData?._id]);

  // Toggle node expansion
  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  // Toggle all nodes
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedNodes({});
    } else {
      const newExpanded = {};
      const expandAllNodes = (node) => {
        if (!node) return;
        newExpanded[node.id] = true;
        node.children?.forEach(expandAllNodes);
      };
      expandAllNodes(networkData);
      setExpandedNodes(newExpanded);
    }
    setAllExpanded(!allExpanded);
  };

  // Helper function to check if a node or its children match the search term
  const matchesSearch = (node, term) => {
    if (!term || !node) return true;

    const termLower = term.toLowerCase();
    return (
      (node.name || '').toLowerCase().includes(termLower) ||
      (node.sponsorCode || '').toLowerCase().includes(termLower) ||
      (node.rank || '').toLowerCase().includes(termLower) ||
      (node.email || '').toLowerCase().includes(termLower) ||
      (node.phone || '').toLowerCase().includes(termLower) ||
      (node.city || '').toLowerCase().includes(termLower) ||
      node.children?.some((child) => matchesSearch(child, term))
    );
  };

  const getRankIcon = (rank) => {
    const rankLower = rank?.toLowerCase() || '';
    if (rankLower.includes('director')) return <FaCrown className="w-4 h-4 text-yellow-500" />;
    if (rankLower.includes('manager')) return <FaMedal className="w-4 h-4 text-blue-500" />;
    if (rankLower.includes('executive')) return <FaCertificate className="w-4 h-4 text-purple-500" />;
    return <FiStar className="w-4 h-4 text-gray-500" />;
  };

  const getStatusColor = (status) => {
    return status === 'active' 
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
      : 'bg-red-100 text-red-700 border-red-200';
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? <FiUserCheck className="w-3 h-3" /> : <FiUserX className="w-3 h-3" />;
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const openMemberDetails = (member) => {
    setSelectedMember(member);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMember(null);
  };

  // Render network row
  const renderNetworkRow = (node, level = 0) => {
    if (!node || !matchesSearch(node, searchTerm)) return null;

    const isExpanded = expandedNodes[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const paddingLeft = Math.min(level * 1.5, 6);

    return (
      <React.Fragment key={node.id}>
        <motion.tr
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className={`hover:bg-gray-50 transition-colors cursor-pointer ${level > 0 ? 'bg-gray-50/30' : ''}`}
          onClick={() => openMemberDetails(node)}
        >
          <td className="px-4 py-3 whitespace-nowrap" style={{ paddingLeft: `${1 + paddingLeft}rem` }}>
            <div className="flex items-center gap-2">
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition"
                >
                  {isExpanded ? (
                    <FiChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <FiChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              )}
              {!hasChildren && <div className="w-6" />}
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                <FaUserCircle className="w-4 h-4 text-indigo-500" />
              </div>
              <span className="font-mono text-sm font-semibold text-indigo-600">
                {node.sponsorCode}
              </span>
            </div>
           </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div>
              <span className="font-medium text-gray-800">{node.name}</span>
              <div className="flex items-center gap-1 mt-0.5">
                {getRankIcon(node.rank)}
                <span className="text-xs text-gray-500">{node.rank}</span>
              </div>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
              {getRankIcon(node.rank)}
              {node.rank}
            </span>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="flex items-center gap-1 text-gray-600">
              <FiPhone className="w-3 h-3 text-gray-400" />
              <span className="text-sm">{node.phone}</span>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="flex items-center gap-1 text-gray-600">
              <FiMapPin className="w-3 h-3 text-gray-400" />
              <span className="text-sm">{node.city}</span>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="flex items-center gap-1 text-gray-600">
              <FiMail className="w-3 h-3 text-gray-400" />
              <span className="text-sm truncate max-w-[150px]">{node.email}</span>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(node.status)}`}>
              {getStatusIcon(node.status)}
              {node.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="text-sm font-semibold text-gray-800">{formatAmount(node.business)}</span>
          </td>
        </motion.tr>
        {isExpanded && node.children?.map((child) => renderNetworkRow(child, level + 1))}
      </React.Fragment>
    );
  };

  const hasSearchResults = (node) => node && matchesSearch(node, searchTerm);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FaNetworkWired className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading network structure...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-8 shadow-xl max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <FiUsers className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Network</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Network Explorer
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Visualize and manage your network connections</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Live Network</span>
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Members</p>
                <p className="text-3xl font-bold text-gray-800">{networkStats.totalMembers}</p>
                <p className="text-xs text-gray-400 mt-1">In your network</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FaTree className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Direct Referrals</p>
                <p className="text-3xl font-bold text-emerald-600">{networkStats.directReferrals}</p>
                <p className="text-xs text-gray-400 mt-1">Level 1 members</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiUserPlus className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Your Sponsor</p>
                <p className="text-xl font-bold text-blue-600 truncate">{networkStats.yourSponsor}</p>
                <p className="text-xs text-gray-400 mt-1">Upline member</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <FaHandshake className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Your Rank</p>
                <p className="text-2xl font-bold text-purple-600">{networkStats.yourRank}</p>
                <p className="text-xs text-gray-400 mt-1">Current position</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <FiAward className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Actions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, sponsor code, rank, email, phone, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 transition"
                >
                  {allExpanded ? (
                    <>
                      <FiChevronDown className="w-4 h-4" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <FiChevronRight className="w-4 h-4" />
                      Expand All
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Network Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sponsor Code</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name / Rank</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rank</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">City</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Business</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {networkData && renderNetworkRow(networkData)}
              </tbody>
            </table>

            {/* No Results Message */}
            {networkData && searchTerm && !hasSearchResults(networkData) && (
              <div className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <FiSearch className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-gray-600 font-medium text-lg">No matches found</h3>
                <p className="text-gray-500 text-sm mt-1">Try adjusting your search query</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Member Details Modal */}
      <AnimatePresence>
        {showModal && selectedMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <FaUserCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">{selectedMember.name}</h2>
                      <p className="text-indigo-100 text-sm">{selectedMember.sponsorCode}</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="text-white hover:bg-white/20 rounded-lg p-1 transition">
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Rank</p>
                    <div className="flex items-center gap-1 mt-1">
                      {getRankIcon(selectedMember.rank)}
                      <p className="text-sm font-semibold text-gray-800">{selectedMember.rank}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Status</p>
                    <span className={`inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedMember.status)}`}>
                      {getStatusIcon(selectedMember.status)}
                      {selectedMember.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-800 break-all">{selectedMember.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-800">{selectedMember.phone}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">City</p>
                    <p className="text-sm font-medium text-gray-800">{selectedMember.city}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">State/Country</p>
                    <p className="text-sm font-medium text-gray-800">{selectedMember.state}, {selectedMember.country}</p>
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-semibold text-indigo-800">Total Business</p>
                    <p className="text-xl font-bold text-indigo-600">{formatAmount(selectedMember.business)}</p>
                  </div>
                </div>

                {selectedMember.children?.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-semibold text-blue-800">Direct Referrals</p>
                      <p className="text-xl font-bold text-blue-600">{selectedMember.children.length}</p>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Members sponsored by this person</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NetworkExplorer;